import { zodResolver } from '@hookform/resolvers/zod';
import { type RegisteredRouter, useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Path, useForm } from 'react-hook-form';
import { type z, ZodEffects, ZodNumber, ZodOptional, ZodString } from 'zod';

import { FORM_SEARCH_PARAMS_SYNC_DEBOUNCE_TIME } from '../constants';
import { debounce } from '../tools/helpers';

type BaseZodType = z.ZodString | z.ZodNumber | z.ZodDate | z.ZodBoolean;
type SchemaEntryValidator =
  | BaseZodType
  | z.ZodOptional<BaseZodType>
  | z.ZodEffects<BaseZodType>
  | z.ZodEffects<z.ZodOptional<BaseZodType>, any, unknown>;

const narrowSchema = (
  schema: SchemaEntryValidator,
): { schema: BaseZodType; wasOptional: boolean; wasEffect: boolean } => {
  if (schema instanceof ZodOptional) {
    return { schema: schema._def.innerType, wasEffect: false, wasOptional: true };
  }

  if (schema instanceof ZodEffects) {
    const tempSchema = schema._def.schema;
    return {
      schema: tempSchema instanceof ZodOptional ? tempSchema._def.innerType : tempSchema,
      wasEffect: true,
      wasOptional: tempSchema instanceof ZodOptional ? true : false,
    };
  }

  return {
    schema,
    wasOptional: false,
    wasEffect: false,
  };
};

const isEmptySearchValue = (value: unknown) =>
  value === undefined || value === null || value === '';

/**
 * The form's fields as they stand in a search, compared by what they ask for: '' and a missing
 * param are the same, and a Date matches the ISO string the URL holds for it.
 */
const searchKeyFor = (keys: ReadonlyArray<string>, values: Record<string, unknown>) =>
  JSON.stringify(
    [...keys]
      .sort()
      .filter((key) => !isEmptySearchValue(values[key]))
      .map((key) => [key, values[key]]),
  );

const isSameSearchValue = (first: unknown, second: unknown) =>
  first === second || JSON.stringify(first) === JSON.stringify(second);

/**
 *
 * @param schema - will be used to parse the search params and generate default values form it
 * @param defaultValues - will be used as a base object. Please make sure to memoize it properly!!!
 */
export const useGetDefaultSearchFormValues = <
  SchemaType extends z.ZodObject<
    { [x: string]: SchemaEntryValidator },
    any,
    any,
    {
      [x: string]: any;
    },
    {
      [x: string]: any;
    }
  >,
>(
  schema: SchemaType,
  defaultValues?: z.infer<SchemaType>,
) => {
  const searchParams = useSearch({ strict: false });
  const [defaultValueFromSearchParams] = useState(() => {
    const parsedSchema: Partial<z.infer<SchemaType>> = {};
    Object.entries(schema.shape).forEach(([key, schema]) => {
      const value = searchParams[key as keyof typeof searchParams];
      if (typeof value === 'undefined') return;
      const parsedValue = value;
      Object.assign(parsedSchema, { [key]: parsedValue });
    });

    return parsedSchema;
  });

  return useMemo(
    () => ({ ...defaultValues, ...defaultValueFromSearchParams }),
    [defaultValueFromSearchParams, defaultValues],
  );
};

export const useSearchParamsForm = <
  SchemaType extends z.ZodObject<
    { [x: string]: SchemaEntryValidator },
    any,
    any,
    {
      [x: string]: any;
    },
    {
      [x: string]: any;
    }
  >,
>({
  schema,
  disableParamsSync = false,
  defaultResetValues,
  defaultValues,
  ...params
}: { schema: SchemaType; defaultResetValues?: Partial<z.infer<SchemaType>> } & Parameters<
  typeof useForm<z.infer<SchemaType>>
>['0'] & {
    disableParamsSync?: boolean;
  }) => {
  type SchemaObject = z.infer<SchemaType>;
  const form = useForm<SchemaObject>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
    ...params,
  });

  const initializationRef = useRef(false);
  const schemaKeys = useMemo(() => Object.keys(schema.shape), [schema.shape]);

  // The form writes to the URL after a debounce, so for a moment the URL is behind the form. A
  // URL change in that moment is either the form's own write landing, a change to params the
  // form doesn't own (a page reset, a sort), or the user going somewhere else (Back, Forward, a
  // link). The first two must not undo what was just typed; the last must win over it.
  // `lastWrittenSearchKeyRef` is the form's part of the URL as the form last wrote it (or found
  // it): any other value is a change from outside.
  const lastWrittenSearchKeyRef = useRef<string | null>(null);
  const isWritePendingRef = useRef(false);
  const cancelPendingWriteRef = useRef<() => void>(() => undefined);
  // Back, Forward and Go always come from outside, even when they land on the same filters.
  const historyTraversedRef = useRef(false);
  // Values set on the form from the URL are not written back to it.
  const isSyncingFromUrlRef = useRef(false);

  const searchParams = useSearch({ strict: false });
  const navigate = useNavigate();
  // Null outside a router (some screen tests mock only useSearch and useNavigate); Back and
  // Forward are then still caught by the filters changing.
  const history = (useRouter({ warn: false }) as RegisteredRouter | null)?.history;

  useEffect(() => {
    if (!history) return;
    return history.subscribe(({ action }) => {
      if (action.type === 'PUSH' || action.type === 'REPLACE') return;
      historyTraversedRef.current = true;
      isWritePendingRef.current = false;
      cancelPendingWriteRef.current();
    });
  }, [history]);

  // sync form with search params
  useEffect(() => {
    const urlSearchKey = searchKeyFor(schemaKeys, searchParams);
    const isExternalChange =
      historyTraversedRef.current ||
      (lastWrittenSearchKeyRef.current !== null &&
        urlSearchKey !== lastWrittenSearchKeyRef.current);
    historyTraversedRef.current = false;

    if (isExternalChange) {
      isWritePendingRef.current = false;
      cancelPendingWriteRef.current();
    } else if (isWritePendingRef.current) {
      return;
    }
    lastWrittenSearchKeyRef.current = urlSearchKey;

    const currentFormValues = form.getValues();
    const currentSearchParamsValues = searchParams;
    isSyncingFromUrlRef.current = true;
    try {
      // Every field the schema has, not only those the form holds now: the first reset from the
      // URL leaves out fields the URL didn't have, and a later URL can still set them.
      schemaKeys.forEach((key) => {
        const value = currentFormValues[key as keyof typeof currentFormValues];
        const searchParamValue =
          currentSearchParamsValues[key as keyof typeof currentSearchParamsValues];
        if (searchParamValue === undefined) {
          if (value === undefined) return;
          if (typeof defaultValues === 'object') {
            // An empty text or zero number already reads as "no filter".
            const { schema: zodSchema } = narrowSchema(schema.shape[key]);
            if (
              (zodSchema instanceof ZodString && value === '') ||
              (zodSchema instanceof ZodNumber && value === 0)
            ) {
              return;
            }
            const newValue = defaultValues?.[key] as SchemaObject[keyof SchemaObject];
            if (isSameSearchValue(value, newValue)) return;
            form.setValue(key as Path<SchemaObject>, newValue);
          }

          return;
        }

        if (isSameSearchValue(searchParamValue, value)) return;
        form.setValue(
          key as Path<SchemaObject>,
          searchParamValue as SchemaObject[keyof SchemaObject],
        );
      });
    } finally {
      isSyncingFromUrlRef.current = false;
    }
  }, [defaultValues, form, schema.shape, schemaKeys, searchParams]);

  // sync search params with form
  useEffect(() => {
    if (disableParamsSync) return;

    const writeToUrl = debounce((data: Partial<SchemaObject>) => {
      isWritePendingRef.current = false;
      // A Back that landed on the same search never reached the sync above; it is over now.
      historyTraversedRef.current = false;
      // Merged into the URL as it is when the write lands, not as it was when typing began:
      // a page or sort change made during the debounce must not be written back over. What is
      // remembered is the merged result, since the URL can hold form fields the form left out.
      void navigate({
        to: '.',
        search: ((previous: Record<string, unknown>) => {
          const next = { ...previous, ...data };
          lastWrittenSearchKeyRef.current = searchKeyFor(schemaKeys, next);
          return next;
        }) as never,
      });
    }, FORM_SEARCH_PARAMS_SYNC_DEBOUNCE_TIME);
    cancelPendingWriteRef.current = () => writeToUrl.cancel();
    const subscription = form.watch((data) => {
      if (isSyncingFromUrlRef.current) return;
      isWritePendingRef.current = true;
      writeToUrl(data as Partial<SchemaObject>);
    });

    return () => {
      subscription.unsubscribe();
      // A write still waiting when the form goes away would land on whatever page is next.
      writeToUrl.cancel();
      isWritePendingRef.current = false;
      cancelPendingWriteRef.current = () => undefined;
    };
  }, [disableParamsSync, form, navigate, schemaKeys]);

  // initialize form with search params
  useEffect(() => {
    if (!initializationRef.current && !disableParamsSync) {
      const parsedSchema: Partial<z.infer<SchemaType>> = {};
      Object.entries(schema.shape).forEach(([key, schema]) => {
        const value = searchParams[key as keyof typeof searchParams];
        if (!value) return;
        const parsedValue = value;
        Object.assign(parsedSchema, { [key]: parsedValue });
      });
      isSyncingFromUrlRef.current = true;
      try {
        form.reset(parsedSchema);
      } finally {
        isSyncingFromUrlRef.current = false;
      }
      initializationRef.current = true;
    }
  }, [disableParamsSync, form, schema.shape, searchParams]);

  const _reset = useMemo(() => {
    if (!defaultResetValues) return form.reset;

    return (
      values?: Parameters<typeof form.reset>['0'],
      keepStateOptions?: Parameters<typeof form.reset>['1'],
    ) => {
      form.reset({ ...defaultResetValues, ...values }, keepStateOptions);
    };
  }, [defaultResetValues, form]);

  return useMemo(() => ({ ...form, reset: _reset }), [_reset, form]);
};
